/**
 * Utilities for generating and handling heading anchors in Answer blocks
 */

/**
 * Convert heading text to URL-safe slug
 * Example: "Introduction to AI" -> "introduction-to-ai"
 */
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Create a unique anchor ID for a heading
 * Format: {messageId}-{slug} with numeric suffix for duplicates
 * Example: "message_abc123-introduction" or "message_abc123-summary-2"
 */
export function createAnchorId(messageId: string, headingText: string, existingIds: Set<string>): string {
  const slug = generateSlug(headingText);
  const baseId = `${messageId}-${slug}`;

  // Handle duplicates by adding numeric suffix
  let anchorId = baseId;
  let counter = 2;
  while (existingIds.has(anchorId)) {
    anchorId = `${baseId}-${counter}`;
    counter++;
  }

  return anchorId;
}

/**
 * Copy a full URL with anchor to clipboard
 * Shows success toast notification
 */
export async function copyAnchorLink(anchorId: string): Promise<void> {
  const url = `${window.location.origin}${window.location.pathname}#${anchorId}`;

  try {
    await navigator.clipboard.writeText(url);
    // TODO(amalucelli): Add toast notification when toast system is available
    console.log("Link copied to clipboard:", url);
  } catch (err) {
    console.error("Failed to copy link:", err);
  }
}

/**
 * Scroll to a heading with the given anchor ID
 * Optionally highlights the heading temporarily
 */
export function scrollToAnchor(anchorId: string, highlight = true): void {
  const element = document.getElementById(anchorId);
  if (!element) {
    console.warn(`Element with id "${anchorId}" not found`);
    return;
  }

  // Smooth scroll to element
  element.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });

  // Add highlight effect
  if (highlight) {
    element.classList.add("heading-anchor-highlight");

    // Remove highlight after animation completes
    setTimeout(() => {
      element.classList.remove("heading-anchor-highlight");
    }, 2500);
  }
}
